/**
 * Country helpers. A school's country is any ISO 3166-1 alpha-2 code: there is no list of supported countries here.
 *
 * The complete list, with each country's currencies and time zones, is served by the API
 * (`GET /user-access/tenants/countries`) from the server's country dataset, so the console and the server always
 * agree on what a country is. Names for display come from the browser's own ISO data (`Intl.DisplayNames`).
 *
 * Phone numbers are a separate concern: see `utils/phone.ts`, which reads libphonenumber's metadata and uses the
 * school's country only as the default region for a number written without its country code.
 */

/** An ISO 3166-1 alpha-2 code, e.g. `AE`. */
export type CountryCode = string;

/** The platform default when no country is known, mirroring `IsoCountries.PlatformDefault` on the server. */
export const DEFAULT_COUNTRY_CODE: CountryCode = "IN";

const ALPHA2 = /^[A-Za-z]{2}$/;

const regionNames =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region", fallback: "code" })
    : undefined;

/** Normalizes a value to an upper-case alpha-2 code, or undefined when it is not shaped like one. */
export function normalizeCountryCode(
  code: string | undefined | null,
): CountryCode | undefined {
  const trimmed = code?.trim();
  return trimmed && ALPHA2.test(trimmed) ? trimmed.toUpperCase() : undefined;
}

/** Normalizes a country code, falling back to the platform default. */
export function countryCodeOrDefault(code: string | undefined | null): CountryCode {
  return normalizeCountryCode(code) ?? DEFAULT_COUNTRY_CODE;
}

/** The English name of a country, e.g. `United Arab Emirates`, or the code itself when the browser has no name. */
export function countryName(code: string | undefined | null): string {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return code ?? "";

  try {
    return regionNames?.of(normalized) ?? normalized;
  } catch {
    return normalized;
  }
}

/** The regional-indicator flag emoji for a country code, e.g. 🇦🇪. */
export function countryFlag(code: string | undefined | null): string {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return "";

  return String.fromCodePoint(
    ...[...normalized].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
  );
}
